/**
 * iter150AZ.79 — TimeSelect: a custom time-picker dropdown for the
 * sequencing Weekly Stop Schedule editor.
 *
 * The browser's native ``<input type="time">`` dropdown is NOT
 * customisable — we cannot disable individual hours or minutes in
 * it. To prevent the user from selecting overlapping times we
 * replace the native input with this component:
 *
 *   • Trigger button shows the current "HH:MM" + a clock icon.
 *   • Click opens a two-column popover (hours 00-23 | minutes 00-59).
 *   • Each cell is a button. The caller-supplied ``isDisabled(time)``
 *     predicate decides which cells render as greyed and unclickable.
 *   • An HOUR is greyed iff EVERY minute of that hour is disabled
 *     (the whole hour is blocked).
 *   • A MINUTE is greyed iff the (currentHour, thisMinute)
 *     combination is disabled.
 *
 * The component is keyboard-accessible (buttons can be tabbed /
 * Enter-activated) and uses native scrolling for the two columns.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Clock, Warning } from "@phosphor-icons/react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

function _pad(n) { return String(n).padStart(2, "0"); }

export default function TimeSelect({
  value,
  onChange,
  /** ``(timeStr) => boolean`` — return true to grey-out & disable
   *  the cell that would result in this exact ``HH:MM`` value. */
  isDisabled,
  disabled = false,
  /** Cosmetic label shown above the popover. */
  hint,
  testId,
  invalid = false,
}) {
  const [open, setOpen] = useState(false);
  const safeValue = (value && /^\d{1,2}:\d{2}$/.test(value)) ? value : "00:00";
  const [hStr, mStr] = safeValue.split(":");
  const curH = Math.max(0, Math.min(23, Number(hStr) || 0));
  const curM = Math.max(0, Math.min(59, Number(mStr) || 0));
  const hCol = useRef(null);
  const mCol = useRef(null);

  // Pre-compute which hours and which minutes are blocked. Hours
  // depend on a full minute-sweep so this is O(24 * 60) = 1440
  // predicate calls per render — fine for any sane predicate.
  const hourBlocked = useMemo(() => {
    if (!isDisabled) return new Array(24).fill(false);
    const out = new Array(24);
    for (let h = 0; h < 24; h++) {
      let allBlocked = true;
      for (let m = 0; m < 60; m++) {
        if (!isDisabled(`${_pad(h)}:${_pad(m)}`)) { allBlocked = false; break; }
      }
      out[h] = allBlocked;
    }
    return out;
  }, [isDisabled]);

  const minuteBlocked = useMemo(() => {
    if (!isDisabled) return new Array(60).fill(false);
    const hh = _pad(curH);
    const out = new Array(60);
    for (let m = 0; m < 60; m++) {
      out[m] = isDisabled(`${hh}:${_pad(m)}`);
    }
    return out;
  }, [isDisabled, curH]);

  // Scroll the selected row into view whenever the popover opens.
  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => {
      try {
        hCol.current?.querySelector(`[data-h="${curH}"]`)
                    ?.scrollIntoView({ block: "center" });
        mCol.current?.querySelector(`[data-m="${curM}"]`)
                    ?.scrollIntoView({ block: "center" });
      } catch { /* nonfatal */ }
    }, 30);
    return () => clearTimeout(t);
  }, [open, curH, curM]);

  function pickHour(h) {
    if (hourBlocked[h]) return;
    // If the current minute is still blocked under the new hour,
    // walk forward to find the first free minute so the resulting
    // value isn't immediately invalid.
    const hh = _pad(h);
    let m = curM;
    if (isDisabled && isDisabled(`${hh}:${_pad(m)}`)) {
      let found = false;
      for (let i = 0; i < 60; i++) {
        const cand = (m + i) % 60;
        if (!isDisabled(`${hh}:${_pad(cand)}`)) { m = cand; found = true; break; }
      }
      if (!found) {
        // Should never happen — hour wasn't blocked but no minute is
        // free? Bail without committing.
        return;
      }
    }
    onChange(`${hh}:${_pad(m)}`);
  }

  function pickMinute(m) {
    if (minuteBlocked[m]) return;
    onChange(`${_pad(curH)}:${_pad(m)}`);
    setOpen(false);
  }

  return (
    <Popover open={open} onOpenChange={(o) => !disabled && setOpen(o)}>
      <PopoverTrigger asChild>
        <button type="button"
                data-testid={testId}
                disabled={disabled}
                aria-label={hint ? `${hint}: ${safeValue}` : safeValue}
                data-invalid={invalid ? "1" : "0"}
                className={`text-xs px-2 py-1 border rounded-sm font-mono inline-flex items-center gap-1.5 bg-white disabled:opacity-40 disabled:cursor-not-allowed ${
                  invalid ? "border-rose-400 ring-1 ring-rose-200 text-rose-900"
                          : "border-slate-300 hover:border-slate-400 text-slate-900"
                }`}>
          <span>{safeValue}</span>
          <Clock size={12} weight="regular" className="text-slate-400" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={4}
        className="w-auto p-0 rounded-sm border border-slate-300 shadow-lg"
        data-testid={testId ? `${testId}-popover` : undefined}>
        {hint && (
          <div className="px-3 py-1.5 text-[10px] uppercase tracking-wider font-bold text-slate-500 border-b border-slate-200 bg-slate-50">
            {hint}
          </div>
        )}
        <div className="flex" style={{ height: "240px" }}>
          {/* Hours column */}
          <div ref={hCol}
               className="overflow-y-auto border-r border-slate-200"
               data-testid={testId ? `${testId}-hours` : undefined}>
            {Array.from({ length: 24 }, (_, h) => {
              const isSelected = h === curH;
              const isBlocked  = hourBlocked[h];
              return (
                <button key={h}
                        type="button"
                        data-h={h}
                        data-testid={testId ? `${testId}-h-${h}` : undefined}
                        data-blocked={isBlocked ? "1" : "0"}
                        disabled={isBlocked}
                        onClick={() => pickHour(h)}
                        title={isBlocked ? "All minutes in this hour would overlap another window" : ""}
                        className={`block w-14 text-center px-3 py-1.5 text-sm font-mono ${
                          isBlocked
                            ? "text-slate-300 line-through bg-slate-50 cursor-not-allowed"
                            : isSelected
                              ? "bg-blue-600 text-white font-bold"
                              : "text-slate-900 hover:bg-slate-100 cursor-pointer"
                        }`}>
                  {_pad(h)}
                </button>
              );
            })}
          </div>
          {/* Minutes column */}
          <div ref={mCol}
               className="overflow-y-auto"
               data-testid={testId ? `${testId}-minutes` : undefined}>
            {Array.from({ length: 60 }, (_, m) => {
              const isSelected = m === curM;
              const isBlocked  = minuteBlocked[m];
              return (
                <button key={m}
                        type="button"
                        data-m={m}
                        data-testid={testId ? `${testId}-m-${m}` : undefined}
                        data-blocked={isBlocked ? "1" : "0"}
                        disabled={isBlocked}
                        onClick={() => pickMinute(m)}
                        title={isBlocked ? `${_pad(curH)}:${_pad(m)} would overlap another window` : ""}
                        className={`block w-14 text-center px-3 py-1.5 text-sm font-mono ${
                          isBlocked
                            ? "text-slate-300 line-through bg-slate-50 cursor-not-allowed"
                            : isSelected
                              ? "bg-blue-600 text-white font-bold"
                              : "text-slate-900 hover:bg-slate-100 cursor-pointer"
                        }`}>
                  {_pad(m)}
                </button>
              );
            })}
          </div>
        </div>
        <div className="px-3 py-1.5 text-[10px] text-slate-500 border-t border-slate-200 bg-slate-50 inline-flex items-center gap-1">
          <Warning size={10} weight="fill" className="text-slate-300" />
          Greyed-out times would overlap another stop window
        </div>
      </PopoverContent>
    </Popover>
  );
}
